import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import PageHeader from '../../components/layout/PageHeader';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import DatePicker from '../../components/ui/DatePicker';
import Textarea from '../../components/ui/Textarea';
import { listWorkflowCategories, listDocumentTypes } from '../../api/workflow';
import { listNationalities } from '../../api/masters';
import { createCandidate } from '../../api/candidates';
import { uploadDocument } from '../../api/documents';
import { pushToast } from '../../components/ui/toastBus';
import { OptionCards, VEHICLE_OPTIONS, LOCATION_OPTIONS, LICENSE_OPTIONS } from '../../components/candidates/OptionCards';
import DynamicField from '../../components/fields/DynamicField';

const EMPTY_PROFILE = {
  full_name: '',
  email: '',
  contact_no: '',
  whatsapp_no: '',
  home_country_contact_no: '',
  nationality_id: '',
  date_of_birth: '',
  passport_no: '',
  passport_expiry_date: '',
  passport_expiry_overridden: false,
  old_emirates_id_no: '',
  referred_agent_name: '',
  has_rta_permit: false,
  rta_permit_details: '',
  visa_type: '',
  visa_validity: '',
  uid_no: '',
  uae_license_no: '',
  uae_license_expiry: '',
  home_license_no: '',
  home_license_expiry: '',
};

export default function CandidateRegister() {
  const navigate = useNavigate();
  const categoriesQuery = useQuery({ queryKey: ['workflow-categories'], queryFn: listWorkflowCategories });
  const documentTypesQuery = useQuery({ queryKey: ['document-types'], queryFn: listDocumentTypes });
  const nationalitiesQuery = useQuery({ queryKey: ['nationalities'], queryFn: () => listNationalities() });

  const [vehicleType, setVehicleType] = useState(null);
  const [location, setLocation] = useState(null);
  const [licenseType, setLicenseType] = useState(null);
  const [profile, setProfile] = useState(EMPTY_PROFILE);
  const [answers, setAnswers] = useState({});
  const [nationalityAnswers, setNationalityAnswers] = useState({});
  const [errors, setErrors] = useState({});
  const [documentFiles, setDocumentFiles] = useState({});
  const [uploading, setUploading] = useState(false);

  const category = useMemo(
    () =>
      categoriesQuery.data?.find(
        (c) => c.vehicle_type === vehicleType && c.location === location && c.license_type === licenseType
      ),
    [categoriesQuery.data, vehicleType, location, licenseType]
  );

  const registrationStep = category?.steps?.find((s) => s.code === 'registration');
  const registrationRequiredDocs = (registrationStep?.required_documents || []).map((d) => d.code);
  const registrationFields = registrationStep?.fields || [];
  const docTypeName = (code) => documentTypesQuery.data?.find((t) => t.code === code)?.name || code;

  const nationalities = nationalitiesQuery.data || [];
  const selectedNationality = nationalities.find((n) => String(n.id) === String(profile.nationality_id));
  const nationalityFields = selectedNationality?.fields || [];
  const nationalityDocs = (selectedNationality?.documents || []).map((d) => d.code);
  // Registration-step docs first, then any this nationality adds that aren't already listed.
  const requiredDocCodes = [...new Set([...registrationRequiredDocs, ...nationalityDocs])];

  const passportWarning =
    profile.passport_expiry_date &&
    new Date(profile.passport_expiry_date) < new Date(Date.now() + 365 * 24 * 60 * 60 * 1000);

  const mutation = useMutation({
    mutationFn: (payload) => createCandidate(payload),
    onSuccess: async (candidate) => {
      const filesToUpload = Object.entries(documentFiles).filter(([, file]) => file);

      if (filesToUpload.length > 0) {
        setUploading(true);
        for (const [code, file] of filesToUpload) {
          const docType = documentTypesQuery.data?.find((t) => t.code === code);
          if (!docType) continue;

          const formData = new FormData();
          formData.append('file', file);
          formData.append('document_type_id', docType.id);

          try {
            await uploadDocument(candidate.id, formData);
          } catch {
            pushToast({ type: 'error', message: `Could not upload ${docType.name}. You can add it later from the candidate's profile.` });
          }
        }
        setUploading(false);
      }

      pushToast({ type: 'success', message: `Candidate registered: ${candidate.reference_no}` });
      navigate(`/candidates/${candidate.id}`);
    },
    onError: (err) => setErrors(err.response?.data?.errors || {}),
  });

  const set = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setProfile((p) => ({ ...p, [field]: value }));
  };

  const setDocumentFile = (code) => (e) => {
    setDocumentFiles((prev) => ({ ...prev, [code]: e.target.files[0] || null }));
  };

  const setAnswer = (fieldId, value) => setAnswers((prev) => ({ ...prev, [fieldId]: value }));
  const setNationalityAnswer = (fieldId, value) => setNationalityAnswers((prev) => ({ ...prev, [fieldId]: value }));

  const onNationalityChange = (e) => {
    setProfile((p) => ({ ...p, nationality_id: e.target.value }));
    setNationalityAnswers({});
    // Drop any picked files that belonged to the previous nationality's document list.
    setDocumentFiles((prev) =>
      Object.fromEntries(Object.entries(prev).filter(([code]) => registrationRequiredDocs.includes(code)))
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!category) return;
    const payload = { ...profile, workflow_category_id: category.id };
    if (registrationFields.length > 0) payload.answers = answers;
    if (nationalityFields.length > 0) payload.nationality_fields = nationalityAnswers;
    if (location !== 'inside_uae') {
      delete payload.visa_type;
      delete payload.visa_validity;
      delete payload.uid_no;
    }
    if (licenseType === 'uae' && !payload.uae_license_no) {
      // uae-only category: no home license fields needed
      delete payload.home_license_no;
      delete payload.home_license_expiry;
    }
    mutation.mutate(payload);
  };

  const fieldError = (field) => errors[field]?.[0];

  return (
    <div className="w-full">
      <PageHeader title="Register Candidate" />

      <Card>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <h3 className="mb-3 text-sm font-semibold text-gray-900">Category</h3>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
              <div>
                <h4 className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">Vehicle Type</h4>
                <OptionCards options={VEHICLE_OPTIONS} value={vehicleType} onChange={setVehicleType} />
              </div>
              <div>
                <h4 className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">Location</h4>
                <OptionCards options={LOCATION_OPTIONS} value={location} onChange={setLocation} />
              </div>
              <div>
                <h4 className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">License Type</h4>
                <OptionCards options={LICENSE_OPTIONS} value={licenseType} onChange={setLicenseType} />
              </div>
            </div>

            {vehicleType && location && licenseType && !category && (
              <p className="mt-4 text-sm text-red-600">No matching workflow category found.</p>
            )}
          </div>

          <div className="border-t border-gray-100 pt-4">
            <h3 className="mb-3 text-sm font-semibold text-gray-900">Candidate Details</h3>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <Input label="Full Name (as per Passport/EID)" required value={profile.full_name} onChange={set('full_name')} error={fieldError('full_name')} className="md:col-span-2" />
              <Input label="Email" type="email" value={profile.email} onChange={set('email')} error={fieldError('email')} />
              <Input label="Contact No" required value={profile.contact_no} onChange={set('contact_no')} error={fieldError('contact_no')} />
              <Input label="WhatsApp No" value={profile.whatsapp_no} onChange={set('whatsapp_no')} error={fieldError('whatsapp_no')} />
              <Input label="Home Country / Emergency Contact No" value={profile.home_country_contact_no} onChange={set('home_country_contact_no')} error={fieldError('home_country_contact_no')} />
              <Select label="Nationality" required value={profile.nationality_id} onChange={onNationalityChange} error={fieldError('nationality_id')}>
                <option value="">Select…</option>
                {nationalities.map((n) => (
                  <option key={n.id} value={n.id}>
                    {n.name}
                  </option>
                ))}
              </Select>
              <DatePicker label="Date of Birth" required value={profile.date_of_birth} onChange={set('date_of_birth')} error={fieldError('date_of_birth')} />
              <Input label="Passport No" required value={profile.passport_no} onChange={set('passport_no')} error={fieldError('passport_no')} />

              <div>
                <DatePicker
                  label="Passport Expiry Date"
                  required
                  value={profile.passport_expiry_date}
                  onChange={set('passport_expiry_date')}
                  error={fieldError('passport_expiry_date')}
                />
                {passportWarning && (
                  <div className="mt-2 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800">
                    <p>Passport expires in less than 1 year.</p>
                    <label className="mt-1 flex items-center gap-1.5">
                      <input type="checkbox" checked={profile.passport_expiry_overridden} onChange={set('passport_expiry_overridden')} />
                      Proceed anyway
                    </label>
                  </div>
                )}
              </div>

              <Input label="Old Emirates ID No" value={profile.old_emirates_id_no} onChange={set('old_emirates_id_no')} />
              <Input label="Referred Agent's Name" value={profile.referred_agent_name} onChange={set('referred_agent_name')} />
            </div>
          </div>

          {nationalityFields.length > 0 && (
            <div className="border-t border-gray-100 pt-4">
              <h3 className="mb-3 text-sm font-semibold text-gray-900">Nationality Details</h3>
              <div className="grid grid-cols-1 gap-x-4 gap-y-3 md:grid-cols-3">
                {nationalityFields.map((f) => (
                  <div key={f.id}>
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      {f.label}
                      {f.validation?.required && <span className="text-red-500"> *</span>}
                    </label>
                    <DynamicField field={f} value={nationalityAnswers[f.id]} onChange={(value) => setNationalityAnswer(f.id, value)} />
                    {fieldError(`nationality_fields.${f.id}`) && (
                      <p className="mt-1 text-xs text-red-600">{fieldError(`nationality_fields.${f.id}`)}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {location === 'inside_uae' && (
            <div className="border-t border-gray-100 pt-4">
              <h3 className="mb-3 text-sm font-semibold text-gray-900">Visa Details (Inside UAE)</h3>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <Select label="Visa Type" required value={profile.visa_type} onChange={set('visa_type')} error={fieldError('visa_type')}>
                  <option value="">Select…</option>
                  <option value="visit">Visit</option>
                  <option value="employment">Employment</option>
                </Select>
                <DatePicker label="Visa Validity" value={profile.visa_validity} onChange={set('visa_validity')} />
                <Input label="UID No" value={profile.uid_no} onChange={set('uid_no')} />
              </div>
            </div>
          )}

          <div className="border-t border-gray-100 pt-4">
            <h3 className="mb-3 text-sm font-semibold text-gray-900">License Details</h3>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              {licenseType === 'uae' && (
                <>
                  <Input label="UAE DL No" required value={profile.uae_license_no} onChange={set('uae_license_no')} error={fieldError('uae_license_no')} />
                  <DatePicker label="UAE DL Expiry" required value={profile.uae_license_expiry} onChange={set('uae_license_expiry')} error={fieldError('uae_license_expiry')} />
                </>
              )}
              {licenseType === 'gcc_home' && (
                <>
                  <Input label="Home/GCC DL No" required value={profile.home_license_no} onChange={set('home_license_no')} error={fieldError('home_license_no')} />
                  <DatePicker label="Home/GCC DL Expiry" required value={profile.home_license_expiry} onChange={set('home_license_expiry')} error={fieldError('home_license_expiry')} />
                  <Input label="UAE DL No (if held)" value={profile.uae_license_no} onChange={set('uae_license_no')} />
                  <DatePicker label="UAE DL Expiry (if held)" value={profile.uae_license_expiry} onChange={set('uae_license_expiry')} />
                </>
              )}
            </div>
          </div>

          <div className="border-t border-gray-100 pt-4">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <input type="checkbox" checked={profile.has_rta_permit} onChange={set('has_rta_permit')} />
              Holds an RTA permit with Dubai companies?
            </label>
            {profile.has_rta_permit && (
              <Textarea
                className="mt-2"
                placeholder="Details"
                value={profile.rta_permit_details}
                onChange={set('rta_permit_details')}
              />
            )}
          </div>

          {category && registrationFields.length > 0 && (
            <div className="border-t border-gray-100 pt-4">
              <h3 className="mb-3 text-sm font-semibold text-gray-900">Additional Registration Details</h3>
              <div className="grid grid-cols-1 gap-x-4 gap-y-3 md:grid-cols-3">
                {registrationFields.map((f) => (
                  <div key={f.id}>
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      {f.label}
                      {f.validation?.required && <span className="text-red-500"> *</span>}
                    </label>
                    <DynamicField field={f} value={answers[f.id]} onChange={(value) => setAnswer(f.id, value)} />
                    {fieldError(`answers.${f.id}`) && (
                      <p className="mt-1 text-xs text-red-600">{fieldError(`answers.${f.id}`)}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {requiredDocCodes.length > 0 && (
            <div className="border-t border-gray-100 pt-4">
              <h3 className="mb-1 text-sm font-semibold text-gray-900">Required Documents</h3>
              <p className="mb-3 text-xs text-gray-500">
                Attach these now, or add them later from the candidate's profile.
              </p>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {requiredDocCodes.map((code) => (
                  <div key={code}>
                    <label className="mb-1 block text-sm font-medium text-gray-700">{docTypeName(code)}</label>
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={setDocumentFile(code)}
                      className="block w-full text-sm text-gray-600 file:mr-3 file:rounded-md file:border-0 file:bg-primary-50 file:px-3 file:py-2 file:text-sm file:font-medium file:text-primary hover:file:bg-primary-light"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-end gap-3">
            <Button
              type="submit"
              loading={mutation.isPending || uploading}
              disabled={!category || (passportWarning && !profile.passport_expiry_overridden)}
            >
              Register Candidate
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
